/**
 * ACIL Intelligence - Auth Context v2.1 (Multi-Tenant)
 * Gestiona el estado de autenticacion incluyendo el contexto de cliente activo.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/api/auth/me');
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    const { data } = await api.post('/api/auth/login', {
      email, password, remember_me: rememberMe,
    });
    if (data.status === 'success') {
      setUser(data.user);
    }
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch { /* ignorar */ }
    setUser(null);
  };

  /**
   * Cambia el cliente activo (solo Super Admin).
   * El nuevo client_id queda firmado criptograficamente en el JWT.
   * @param {string|null} clientId - ID del cliente a activar, o null para Vista Global
   */
  const switchClient = useCallback(async (clientId) => {
    const { data } = await api.post('/api/auth/switch-client', { client_id: clientId });
    if (data.status === 'success') {
      // Re-cargar perfil con el nuevo token (que ya tiene el client_id actualizado)
      await checkAuth();
    }
    return data;
  }, [checkAuth]);

  const switchAccount = useCallback(async (accountId) => {
    const { data } = await api.post('/api/auth/switch-account', { account_id: accountId });
    if (data.status === 'success') {
      await checkAuth();
    }
    return data;
  }, [checkAuth]);

  const isSuperAdmin = user?.role?.level >= 4;
  const activeClient = user?.active_client ?? null;
  const activeAccount = user?.active_account ?? null;

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    setUser,
    switchClient,
    switchAccount,
    isSuperAdmin,
    activeClient,
    activeAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export default AuthContext;
